/* =============================================================
   snz-rail.js — makes the house rails' controls real

   Scoped to [id$="__featured_collection_best"],
   [id$="__collection_list_CG7Cfg"] (the two homepage rails) and
   [id$="__snz_pdp_rail"] (the collection rail that every product
   template now carries). Two defects, both in the stock markup
   rather than in the house restyle:

   1. THE ARROWS ONLY WENT FORWARD.
      Dawn's SliderComponent disables the previous button whenever
      currentPage === 1 and re-enables it from update(). In this
      theme update() never runs, so currentPage stayed 1 for the
      life of the page, the previous button kept disabled: true,
      and clicking it did nothing — the "click does not move"
      symptom. Verified: calling sc.update() by hand set currentPage
      to 7, enabled the button, and the next click scrolled back.
      sync() below makes that call, from the slider's scroll event
      AND straight after an arrow click, so the button is never
      left stale even if the scroll event is late or coalesced.

   2. THE DOTS WERE DECORATION, NOT PAGINATION.
      sections/featured-collection.liquid emits a hardcoded THREE
      dots with an inline script that jumps to item 0,
      floor(items/3) or the last item, and picks the active one from
      scroll thresholds of 33% and 75%. At 430px that rail has 8
      slides and 4 pages, so three segments could never line up.
      sections/collection-list.liquid has its own copy emitting one
      dot per collection instead of per page.
      render() rebuilds the segments from real geometry, so the
      count matches the pages, a click lands on the page it points
      at, and the active segment follows the scroll.

   The old inline scripts are left in place: their click handlers die
   with the nodes replaced here, and their scroll handler then
   addresses detached elements and does nothing. To roll back, delete
   this asset and its <script> tag in snippets/meta-tags.liquid.

   10 Sep 2026: added __snz_pdp_rail and the file is now loaded on
   product pages as well as the homepage. Nothing else changed — the
   body was always generic; only MATCH named the sections.

   13 Sep 2026: added __related-products, the "You may also like"
   rail, which is now a carousel too. Two things it needs that the
   other rails do not:

   a. IT ARRIVES LATE AND REPLACES ITSELF. Dawn's
      <product-recommendations> fetches its own markup and swaps it
      in when the section nears the viewport, so on a product page
      the rail may not exist when boot() runs, and when it does
      arrive it is a wholly new subtree with none of the listeners
      attached below. watch() re-runs wire() on that swap; a
      data-snz-wired flag on the slider keeps it to once.

   b. SOME OF ITS SLIDES ARE HIDDEN. A recommendations response is
      one fixed list, so the rail fetches the desktop count and
      sections/related-products.liquid hides the surplus below
      750px. metrics() therefore counts the slides that are
      RENDERED, not the ones in the markup, or the pagination draws
      pages that cannot be scrolled to.

   13 Sep 2026 (later): added __snz_pdp_journal, the product page's
   journal rail. sections/featured-blog.liquid used to draw its own
   8px circle dots and sync them with an inline script; it now emits
   the house empty .slider-dots-nav and this file fills it, so all
   three rails on a product page carry one instrument.
   ============================================================= */
(function () {
  var MATCH = /__(featured_collection_best|collection_list_CG7Cfg|snz_pdp_rail|related-products|snz_pdp_journal)$/;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function wire(section) {
    var sc = section.querySelector('slider-component');
    if (!sc || sc.hasAttribute('data-snz-wired')) return;

    var ul = sc.querySelector('ul[id^="Slider-"]') || sc.querySelector('ul');
    var nav = sc.querySelector('.slider-dots-nav');
    if (!ul || !nav) return;

    var allItems = ul.querySelectorAll('li[id^="Slide-"]');
    if (allItems.length < 2) return;

    /* Set before anything below can mutate the subtree: render()
       rewrites nav.innerHTML, and watch()'s observer would otherwise
       call straight back into here. */
    sc.setAttribute('data-snz-wired', '');

    /* Which slides are actually rendered. The recommendations rail
       fetches one list for both breakpoints and hides the surplus
       below 750px, so this is recomputed on every call — a resize
       changes the answer. */
    function shown() {
      var out = [];
      for (var i = 0; i < allItems.length; i++) {
        if (allItems[i].offsetParent !== null) out.push(allItems[i]);
      }
      return out.length > 1 ? out : null;
    }

    /* Read the real geometry rather than trusting a hardcoded count.
       offset is the pitch from one slide to the next, so it already
       carries the grid gap. */
    function metrics() {
      var items = shown();
      if (!items) return null;
      var offset = items[1].offsetLeft - items[0].offsetLeft;
      if (!offset) return null;
      var perPage = Math.max(1, Math.round(ul.clientWidth / offset));
      return {
        offset: offset,
        perPage: perPage,
        pages: Math.max(1, Math.ceil(items.length / perPage))
      };
    }

    function highlight() {
      var m = metrics();
      if (!m) return;
      var max = ul.scrollWidth - ul.clientWidth;
      var page = max > 0 ? Math.round((ul.scrollLeft / max) * (m.pages - 1)) : 0;
      each(nav.children, function (btn, i) {
        if (i === page) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.classList.remove('is-active');
          btn.removeAttribute('aria-current');
        }
      });
    }

    /* The active segment and the arrows' enabled state both derive
       from scroll position, so they are refreshed together. */
    function sync() {
      highlight();
      try {
        if (typeof sc.update === 'function') sc.update();
      } catch (e) {
        /* never let a slider internal break the page */
      }
    }

    function render() {
      var m = metrics();
      if (!m) return;
      if (nav.getAttribute('data-snz-pages') !== String(m.pages)) {
        nav.innerHTML = '';
        for (var i = 0; i < m.pages; i++) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'slider-dot-btn';
          btn.setAttribute('data-snz-page', String(i));
          btn.setAttribute('aria-label', 'Go to page ' + (i + 1) + ' of ' + m.pages);
          var dot = document.createElement('span');
          dot.className = 'dot';
          btn.appendChild(dot);
          nav.appendChild(btn);
        }
        nav.setAttribute('data-snz-pages', String(m.pages));
      }
      sync();
    }

    nav.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest ? event.target.closest('.slider-dot-btn') : null;
      if (!btn || !nav.contains(btn)) return;
      event.preventDefault();
      var m = metrics();
      if (!m) return;
      var max = ul.scrollWidth - ul.clientWidth;
      var i = parseInt(btn.getAttribute('data-snz-page'), 10) || 0;
      /* Position by proportion of the scrollable distance — the same
         formula highlight() reads back, so the segment you click is
         the one that lights up, and the last always lands flush at
         the end. */
      var left = m.pages > 1 ? (i / (m.pages - 1)) * max : 0;
      ul.scrollTo({ left: left, behavior: 'smooth' });
      settle();
    });

    /* A smooth scroll finishes some time after the click. Re-sync a
       few times across that window rather than trusting one event. */
    function settle() {
      [60, 240, 500, 800].forEach(function (ms) {
        setTimeout(sync, ms);
      });
    }

    each(sc.querySelectorAll('.slider-button'), function (button) {
      button.addEventListener('click', settle);
    });

    ul.addEventListener('scroll', sync, { passive: true });
    if ('onscrollend' in ul) ul.addEventListener('scrollend', sync);

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(render, 150);
    });

    render();
    /* Widths can still be settling when a rail is wired straight
       after an innerHTML swap. render() no-ops when the page count
       is unchanged, so this costs nothing when it was already right. */
    setTimeout(render, 300);
  }

  /* Dawn's product-recommendations replaces its own children once
     its fetch returns, taking every listener under it with it.
     childList on the host only — the deeper churn from render()
     rewriting the dots must not call back into here. */
  function watch(section) {
    var host = section.querySelector('product-recommendations');
    if (!host || typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      wire(section);
    }).observe(host, { childList: true });
  }

  /* 9 Oct 2026 (SNZ-RAIL-LAZY): each rail is wired only when it comes
     within 600px of the viewport. wire() reads offsetParent / offsetLeft,
     which forces a full layout of rails that content-visibility:auto
     (snz-header.css) would otherwise skip, so wiring every rail on load
     cost ~150-220 ms of main thread on a product page. Only the arrows and
     dots wait; the rails themselves are visible either way, and a touch or
     focus inside a rail wires it at once if the observer has not fired. */
  function boot() {
    each(document.querySelectorAll('section[id]'), function (section) {
      if (!MATCH.test(section.id)) return;
      watch(section);
      if (typeof IntersectionObserver !== 'function') {
        wire(section);
        return;
      }
      var done = false;
      function go() {
        if (done) return;
        done = true;
        io.disconnect();
        section.removeEventListener('pointerdown', go, true);
        section.removeEventListener('focusin', go, true);
        wire(section);
      }
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { go(); return; }
        }
      }, { rootMargin: '600px 0px' });
      io.observe(section);
      section.addEventListener('pointerdown', go, true);
      section.addEventListener('focusin', go, true);
    });
  }

  /* window load, not DOMContentLoaded: the sections' own inline dot
     scripts run on DOMContentLoaded and this has to replace their
     nodes afterwards, and the slide widths must be settled before
     metrics() reads them. */
  if (document.readyState === 'complete') {
    boot();
  } else {
    window.addEventListener('load', boot);
  }
})();
